import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  // Configurable durations in milliseconds
  private idleTimeout = (environment.Idle || 1500) * 1000; // time before idle warning
  private idleWarningDuration = 60 * 1000; // countdown warning duration (60s)
  
  private absoluteTimeout = 7200 * 1000; // absolute session limit (2h)
  private absoluteWarningDuration = 300 * 1000; // absolute warning time (5m)

  private idleTimer: any;
  private absoluteTimer: any;
  private countdownTimer: any;

  private isWarningShown = false;
  private isAbsoluteWarningShown = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Listen to storage events for multi-tab synchronization
    window.addEventListener('storage', (event) => this.handleStorageChange(event));
  }

  startMonitoring() {
    if (!this.authService.isAuthenticated()) return;

    this.ngZone.runOutsideAngular(() => {
      this.setupActivityListeners();
      this.resetIdleTimer();
      this.setupAbsoluteTimer();
    });
  }

  stopMonitoring() {
    this.clearAllTimers();
    this.removeActivityListeners();
    this.isWarningShown = false;
    this.isAbsoluteWarningShown = false;
  }

  private setupActivityListeners() {
    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, this.onUserActivity);
    });
  }

  private removeActivityListeners() {
    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.removeEventListener(event, this.onUserActivity);
    });
  }

  private onUserActivity = () => {
    // Throttle activity updates to once every 2 seconds
    const now = Date.now();
    const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);
    if (now - lastActivity > 2000) {
      localStorage.setItem('lastActivity', now.toString());
      this.ngZone.run(() => this.resetIdleTimer());
    }
  };

  private resetIdleTimer() {
    if (this.isWarningShown) return;

    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      this.showIdleWarning();
    }, this.idleTimeout);
  }

  private setupAbsoluteTimer() {
    let sessionStart = parseInt(localStorage.getItem('sessionStart') || '0', 10);
    if (!sessionStart) {
      sessionStart = Date.now();
      localStorage.setItem('sessionStart', sessionStart.toString());
    }

    const timeElapsed = Date.now() - sessionStart;
    const timeRemaining = this.absoluteTimeout - timeElapsed;

    clearTimeout(this.absoluteTimer);

    if (timeRemaining <= 0) {
      this.forceLogout('Absolute session timeout reached');
    } else {
      // Schedule warning
      const warningTime = timeRemaining - this.absoluteWarningDuration;
      if (warningTime > 0) {
        this.absoluteTimer = setTimeout(() => {
          this.showAbsoluteWarning();
        }, warningTime);
      } else {
        this.showAbsoluteWarning();
      }
    }
  }

  private showIdleWarning() {
    this.isWarningShown = true;
    let secondsLeft = this.idleWarningDuration / 1000;

    this.ngZone.run(() => {
      Swal.fire({
        title: 'Session Timeout Warning',
        html: `Your session will expire in <b>${secondsLeft}</b> seconds due to inactivity.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Extend Session',
        cancelButtonText: 'Logout Now',
        allowOutsideClick: false,
        timer: this.idleWarningDuration,
        didOpen: () => {
          this.countdownTimer = setInterval(() => {
            secondsLeft--;
            const content = Swal.getHtmlContainer();
            if (content) {
              const b = content.querySelector('b');
              if (b) b.textContent = secondsLeft.toString();
            }
          }, 1000);
        },
        willClose: () => {
          clearInterval(this.countdownTimer);
        }
      }).then((result) => {
        this.isWarningShown = false;
        if (result.isConfirmed) {
          // Extend session
          localStorage.setItem('lastActivity', Date.now().toString());
          this.resetIdleTimer();
        } else {
          this.authService.logout();
        }
      });
    });

    // If timer expires without interaction
    this.idleTimer = setTimeout(() => {
      if (this.isWarningShown) {
        Swal.close();
        this.forceLogout('Idle timeout reached');
      }
    }, this.idleWarningDuration);
  }

  private showAbsoluteWarning() {
    if (this.isAbsoluteWarningShown) return;
    this.isAbsoluteWarningShown = true;

    const sessionStart = parseInt(localStorage.getItem('sessionStart') || '0', 10);
    const timeRemaining = this.absoluteTimeout - (Date.now() - sessionStart);

    this.ngZone.run(() => {
      Swal.fire({
        title: 'Absolute Session Timeout Warning',
        text: 'Your session will end in 5 minutes. Please save any unsaved work.',
        icon: 'warning',
        confirmButtonText: 'Understood',
        timer: 10000
      });
    });

    // Schedule final absolute logout
    this.absoluteTimer = setTimeout(() => {
      this.forceLogout('Absolute timeout reached');
    }, timeRemaining);
  }

  private forceLogout(reason: string) {
    this.stopMonitoring();
    this.authService.clearLocalSession();
    this.ngZone.run(() => {
      Swal.fire({
        title: 'Session Expired',
        text: 'Your session has expired. Please log in again.',
        icon: 'info',
        confirmButtonText: 'Ok'
      });
    });
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key === 'lastActivity' && event.newValue) {
      this.ngZone.run(() => {
        this.resetIdleTimer();
        if (this.isWarningShown) {
          Swal.close();
          this.isWarningShown = false;
        }
      });
    } else if (event.key === 'logout-event') {
      this.ngZone.run(() => {
        this.stopMonitoring();
        this.authService.clearLocalSession();
      });
    }
  }

  private clearAllTimers() {
    clearTimeout(this.idleTimer);
    clearTimeout(this.absoluteTimer);
    clearInterval(this.countdownTimer);
  }
}
